import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTenant } from '../context/TenantContext';
import checkoutTemplateService from '../services/checkoutTemplateService';

const PAGE_SIZE = 50;

const DEFAULT_TEMPLATE_FORM = {
  id: null,
  code: null,
  name: '',
  description: '',
  sites: 0,
  appliedTo: 0,
  security: '4-digit PIN',
  employeePin: 'ENABLED',
  pinLength: '4-digit',
  orgDefault: false,
  updatedBy: '[Admin name]',
  updatedAtText: '-- days ago',
  logos: { logo1: '', logo2: '' },
  brandingEnabled: false,
  colorScheme: 'System default',
  colors: {
    primary: '#006ED7',
    background: '#1A1F2E',
    surface: '#181F32',
    text: '#FFFFFF',
  },
};

function toSecurityLabel(employeePin, pinLength) {
  if (employeePin === 'DISABLED') return 'Disabled';
  if (pinLength === '8-digit') return '8-digit PIN';
  return '4-digit PIN';
}

function formatLastUpdated(iso) {
  if (!iso) return '-- days ago';
  const time = new Date(iso).getTime();
  if (Number.isNaN(time)) return '-- days ago';
  const diffMs = Date.now() - time;
  const days = Math.max(0, Math.floor(diffMs / (24 * 60 * 60 * 1000)));
  if (days === 0) return 'just now';
  if (days === 1) return '1 day ago';
  return `${days} days ago`;
}

function toPinLabel(pinLength) {
  return pinLength === 8 ? '8-digit' : '4-digit';
}

function fromPinLabel(label) {
  return label === '8-digit' ? 8 : 4;
}

function mapApiTemplate(item) {
  const employeePin = item.employeePinRequired ? 'ENABLED' : 'DISABLED';
  const pinLength = item.employeePinRequired ? toPinLabel(item.pinLength) : 'NONE';
  return {
    id: item.templateId,
    code: item.templateCode,
    name: item.templateName,
    description: item.description || '',
    sites: item.activeOn ?? 0,
    appliedTo: item.appliedTo ?? 0,
    security: toSecurityLabel(employeePin, pinLength),
    employeePin,
    pinLength,
    orgDefault: !!item.orgDefault,
    updatedBy: item.updatedBy || '[Admin name]',
    updatedAtText: formatLastUpdated(item.updatedAt || item.createdAt),
    logos: {
      logo1: item.logo1Url || '',
      logo2: item.logo2Url || '',
    },
    brandingEnabled: !!item.brandingEnabled,
    colorScheme: item.colorScheme || 'System default',
    colors: {
      primary: item.primaryColor || '#006ED7',
      background: item.backgroundColor || '#1A1F2E',
      surface: item.surfaceColor || '#181F32',
      text: item.textColor || '#FFFFFF',
    },
  };
}

function sortTemplatesForList(items, asc) {
  return [...items].sort((a, b) => {
    // Always pin the org default template to the top (Figma behavior).
    if (a.orgDefault !== b.orgDefault) return a.orgDefault ? -1 : 1;
    return (asc ? 1 : -1) * String(a.name || '').localeCompare(String(b.name || ''));
  });
}

function toApiPayload(form) {
  const employeePinRequired = form.employeePin === 'ENABLED';
  return {
    templateName: form.name,
    description: form.description || null,
    orgDefault: !!form.orgDefault,
    employeePinRequired,
    pinLength: employeePinRequired ? fromPinLabel(form.pinLength) : null,
    brandingEnabled: !!form.brandingEnabled,
    logo1Url: form.logos.logo1 || null,
    logo2Url: form.logos.logo2 || null,
    colorScheme: form.colorScheme || 'System default',
    primaryColor: form.colors.primary,
    backgroundColor: form.colors.background,
    surfaceColor: form.colors.surface,
    textColor: form.colors.text,
    updatedBy: '[Admin name]',
  };
}

function KebabMenu({ onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <div className="ctpl-kebab" ref={ref}>
      <button
        type="button"
        className="ctpl-kebab-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <i className="fas fa-ellipsis-v"></i>
      </button>
      {open && (
        <div className="ctpl-kebab-menu" role="menu">
          <button type="button" role="menuitem" className="ctpl-kebab-danger" onClick={() => { setOpen(false); onDelete(); }}>
            <i className="fas fa-trash"></i>
            Delete
          </button>
        </div>
      )}
    </div>
  );
}

function TemplateModal({ open, mode, template, submitting, onClose, onSave }) {
  const isEdit = mode === 'edit';
  const initialTemplate = template || DEFAULT_TEMPLATE_FORM;

  const [form, setForm] = useState(initialTemplate);
  const [snapshot, setSnapshot] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm(initialTemplate);
    setSnapshot(JSON.stringify(initialTemplate));
  }, [open, template]);

  if (!open) return null;

  const dirty = JSON.stringify(form) !== snapshot;
  const canSave = form.name.trim().length > 0 && (isEdit ? dirty : true) && !submitting;
  const brandingDisabled = !form.brandingEnabled;

  return (
    <div className="ctpl-overlay" role="dialog" aria-modal="true">
      <div className="ctpl-modal">
        <div className="ctpl-modal-header">
          <h2>{isEdit ? 'Edit checkout template' : 'Add template'}</h2>
          <p>Configure default settings for camera checkout and user authentication.</p>
        </div>

        <div className="ctpl-default-row">
          <label className="ctpl-check-label">
            <input
              type="checkbox"
              checked={!!form.orgDefault}
              onChange={(e) => setForm((prev) => ({ ...prev, orgDefault: e.target.checked }))}
            />
            <span>Set as organization default</span>
          </label>
          <i
            className="fas fa-question-circle"
            title="Makes this the baseline configuration for your entire organization. Only one template can be the organization default at a time."
          ></i>
        </div>

        <div className="ctpl-modal-body">
          <section className="ctpl-section">
            <h3>Information</h3>
            <div className="ctpl-card">
              <div className="ctpl-field">
                <label>Template name*</label>
                <input
                  type="text"
                  placeholder="Enter name"
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>
              <div className="ctpl-field">
                <label>Description</label>
                <input
                  type="text"
                  placeholder="Enter description"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                />
              </div>
              <div className="ctpl-meta-grid">
                <div>
                  <span>Active on</span>
                  <strong>{form.sites} sites</strong>
                </div>
                <div>
                  <span>Applied to</span>
                  <strong>{form.appliedTo} checkouts</strong>
                </div>
                <div>
                  <span>Last updated</span>
                  <strong>{form.updatedAtText}</strong>
                </div>
                <div>
                  <span>Updated by</span>
                  <strong>{form.updatedBy}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="ctpl-section">
            <h3>Security</h3>
            <div className="ctpl-card ctpl-grid-2">
              <div className="ctpl-field">
                <label>Employee PIN*</label>
                <select
                  value={form.employeePin}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    employeePin: e.target.value,
                    pinLength: e.target.value === 'DISABLED' ? 'NONE' : (prev.pinLength === 'NONE' ? '4-digit' : prev.pinLength),
                  }))}
                >
                  <option value="ENABLED">Enabled</option>
                  <option value="DISABLED">Disabled</option>
                </select>
              </div>
              <div className="ctpl-field">
                <label>PIN length*</label>
                <select
                  value={form.pinLength}
                  disabled={form.employeePin === 'DISABLED'}
                  onChange={(e) => setForm((prev) => ({ ...prev, pinLength: e.target.value }))}
                >
                  <option value="4-digit">4-digit</option>
                  <option value="8-digit">8-digit</option>
                </select>
              </div>
            </div>
          </section>

          <section className="ctpl-section">
            <div className="ctpl-section-title-row">
              <h3>Custom branding</h3>
              <label className="ctpl-switch" aria-label="Toggle custom branding">
                <input
                  type="checkbox"
                  checked={!!form.brandingEnabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, brandingEnabled: e.target.checked }))}
                />
                <span className="ctpl-switch-slider"></span>
              </label>
            </div>
            <div className={`ctpl-card ${brandingDisabled ? 'is-disabled' : ''}`}>
              <div className="ctpl-grid-2">
                <div className="ctpl-field">
                  <label>Logo 1</label>
                  <div className="ctpl-upload-input">
                    <span>{form.logos.logo1 || 'Upload file'}</span>
                    <button
                      type="button"
                      disabled={brandingDisabled}
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        logos: { ...prev.logos, logo1: 'logo-1.png' },
                      }))}
                    >
                      <i className="fas fa-upload"></i>
                    </button>
                  </div>
                </div>
                <div className="ctpl-field">
                  <label>Logo 2</label>
                  <div className="ctpl-upload-input">
                    <span>{form.logos.logo2 || 'Upload file'}</span>
                    <button
                      type="button"
                      disabled={brandingDisabled}
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        logos: { ...prev.logos, logo2: 'logo-2.png' },
                      }))}
                    >
                      <i className="fas fa-upload"></i>
                    </button>
                  </div>
                </div>
              </div>

              <div className="ctpl-field">
                <label>Choose a color scheme</label>
                <select
                  value={form.colorScheme}
                  disabled={brandingDisabled}
                  onChange={(e) => setForm((prev) => ({ ...prev, colorScheme: e.target.value }))}
                >
                  <option value="System default">System default</option>
                  <option value="ColorScheme1">ColorScheme1</option>
                </select>
              </div>

              <div className="ctpl-grid-2">
                <div className="ctpl-field">
                  <label>Primary color</label>
                  <div className="ctpl-color-input">
                    <input
                      type="color"
                      value={form.colors.primary}
                      disabled={brandingDisabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        colors: { ...prev.colors, primary: e.target.value.toUpperCase() },
                      }))}
                    />
                    <span>{form.colors.primary}</span>
                  </div>
                </div>
                <div className="ctpl-field">
                  <label>Background color</label>
                  <div className="ctpl-color-input">
                    <input
                      type="color"
                      value={form.colors.background}
                      disabled={brandingDisabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        colors: { ...prev.colors, background: e.target.value.toUpperCase() },
                      }))}
                    />
                    <span>{form.colors.background}</span>
                  </div>
                </div>
                <div className="ctpl-field">
                  <label>Surface color</label>
                  <div className="ctpl-color-input">
                    <input
                      type="color"
                      value={form.colors.surface}
                      disabled={brandingDisabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        colors: { ...prev.colors, surface: e.target.value.toUpperCase() },
                      }))}
                    />
                    <span>{form.colors.surface}</span>
                  </div>
                </div>
                <div className="ctpl-field">
                  <label>Text color</label>
                  <div className="ctpl-color-input">
                    <input
                      type="color"
                      value={form.colors.text}
                      disabled={brandingDisabled}
                      onChange={(e) => setForm((prev) => ({
                        ...prev,
                        colors: { ...prev.colors, text: e.target.value.toUpperCase() },
                      }))}
                    />
                    <span>{form.colors.text}</span>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <div className="ctpl-modal-footer">
          <button type="button" className="ctpl-btn ctpl-btn-secondary" onClick={onClose} disabled={submitting}>Cancel</button>
          <button
            type="button"
            className="ctpl-btn ctpl-btn-primary"
            disabled={!canSave}
            onClick={() => onSave(form)}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function PublishModal({ open, templateName, appliedTo, submitting, onCancel, onPublish }) {
  if (!open) return null;
  return (
    <div className="ctpl-overlay" role="dialog" aria-modal="true">
      <div className="ctpl-publish-modal">
        <div className="ctpl-publish-header">Publish template updates?</div>
        <div className="ctpl-publish-body">
          This update will be applied to the {appliedTo} checkouts currently using the {templateName} template. This may briefly interrupt active sessions.
        </div>
        <div className="ctpl-publish-footer">
          <button type="button" className="ctpl-btn ctpl-btn-secondary" onClick={onCancel} disabled={submitting}>Cancel</button>
          <button type="button" className="ctpl-btn ctpl-btn-primary" onClick={onPublish} disabled={submitting}>Publish to all</button>
        </div>
      </div>
    </div>
  );
}

export default function CheckoutTemplatesPage() {
  const { orgId } = useTenant();
  const [searchParams, setSearchParams] = useSearchParams();
  const [templates, setTemplates] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sortAsc, setSortAsc] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState(() => new Set());
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState('add');
  const [editing, setEditing] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const [didConsumeDeepLink, setDidConsumeDeepLink] = useState(false);

  const loadTemplates = useCallback(async (q, asc, off) => {
    if (!orgId) {
      setTemplates([]);
      setTotal(0);
      setLoadError('Select an organization to manage checkout templates.');
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const data = await checkoutTemplateService.listTemplates(orgId, {
        q: q || undefined,
        limit: PAGE_SIZE,
        offset: off ?? 0,
        sortBy: 'templateName',
        sortOrder: asc ? 'asc' : 'desc',
      });
      const list = Array.isArray(data) ? data : (data?.content ?? []);
      setTemplates(sortTemplatesForList(list.map(mapApiTemplate), asc));
      setTotal(data?.totalElements ?? list.length);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load checkout templates');
      setTemplates([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    setOffset(0);
    loadTemplates(search, sortAsc, 0);
    setDidConsumeDeepLink(false);
  }, [orgId]); // reload when org changes

  // Debounce search so we don't fire on every keystroke
  useEffect(() => {
    const t = setTimeout(() => {
      setOffset(0);
      loadTemplates(search, sortAsc, 0);
    }, 300);
    return () => clearTimeout(t);
  }, [search, sortAsc]);

  const handleOffsetChange = (newOffset) => {
    setOffset(newOffset);
    loadTemplates(search, sortAsc, newOffset);
  };

  const allChecked = templates.length > 0 && templates.every((item) => selected.has(item.id));
  const someChecked = templates.some((item) => selected.has(item.id));

  const openAdd = () => {
    setMode('add');
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setMode('edit');
    setEditing(row);
    setModalOpen(true);
  };

  const saveTemplate = (form) => {
    setSubmitError('');
    if (mode === 'edit' && editing) {
      setPendingDraft({ ...editing, ...form });
      setModalOpen(false);
      setPublishOpen(true);
      return;
    }

    (async () => {
      if (!orgId) return;
      setSubmitting(true);
      try {
        await checkoutTemplateService.createTemplate(orgId, toApiPayload(form));
        setModalOpen(false);
        setEditing(null);
        await loadTemplates(search, sortAsc, offset);
      } catch (err) {
        setSubmitError(err?.message || 'Failed to create template');
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const publishTemplate = () => {
    if (!pendingDraft) return;

    (async () => {
      if (!orgId) return;
      setSubmitting(true);
      setSubmitError('');
      try {
        await checkoutTemplateService.updateTemplate(orgId, pendingDraft.id, toApiPayload(pendingDraft));
        setPublishOpen(false);
        setPendingDraft(null);
        setEditing(null);
        await loadTemplates(search, sortAsc, offset);
      } catch (err) {
        setSubmitError(err?.message || 'Failed to update template');
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const deleteTemplate = (row) => {
    if (!window.confirm(`Delete template "${row.name}"?`)) return;
    (async () => {
      if (!orgId) return;
      setSubmitting(true);
      setSubmitError('');
      try {
        await checkoutTemplateService.deleteTemplate(orgId, row.id);
        await loadTemplates(search, sortAsc, offset);
      } catch (err) {
        setSubmitError(err?.message || 'Failed to delete template');
      } finally {
        setSubmitting(false);
      }
    })();
  };

  useEffect(() => {
    if (didConsumeDeepLink || templates.length === 0) return;
    const templateId = searchParams.get('templateId');
    const templateCode = searchParams.get('templateCode');
    if (!templateId && !templateCode) {
      setDidConsumeDeepLink(true);
      return;
    }

    const matched = templates.find((t) =>
      (templateId && String(t.id) === String(templateId))
      || (templateCode && String(t.code) === String(templateCode)),
    );

    if (!matched) return;

    setMode('edit');
    setEditing(matched);
    setModalOpen(true);
    setDidConsumeDeepLink(true);

    const next = new URLSearchParams(searchParams);
    next.delete('templateId');
    next.delete('templateCode');
    setSearchParams(next, { replace: true });
  }, [templates, didConsumeDeepLink, searchParams, setSearchParams]);

  return (
    <>
      <div className="ctpl-toolbar">
        <div className="ctpl-toolbar-left">
          <button
            type="button"
            className="ctpl-search-btn"
            aria-label="Search checkout templates"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <i className="fas fa-search"></i>
          </button>

          <div className="ctpl-heading">
            <h1>Checkout templates</h1>
            <span>{total} total</span>
            {searchOpen && (
              <div className="ctpl-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search templates..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>

        <button type="button" className="btn-primary-pill" onClick={openAdd}>
          <i className="fas fa-plus"></i>
          Add template
        </button>
      </div>

      {loadError && <div className="users-error" style={{ marginBottom: 12 }}>{loadError}</div>}
      {submitError && <div className="users-error" style={{ marginBottom: 12 }}>{submitError}</div>}

      <table className="data-table ctpl-table">
        <thead>
          <tr>
            <th className="ctpl-col-check">
              <input
                type="checkbox"
                checked={allChecked}
                ref={(el) => {
                  if (el) el.indeterminate = !allChecked && someChecked;
                }}
                onChange={() => {
                  setSelected((prev) => {
                    const next = new Set(prev);
                    if (allChecked) templates.forEach((item) => next.delete(item.id));
                    else templates.forEach((item) => next.add(item.id));
                    return next;
                  });
                }}
              />
            </th>
            <th>
              <button type="button" className="data-table-sort" onClick={() => setSortAsc((v) => !v)}>
                Name
                <i className={`fas fa-sort-${sortAsc ? 'up' : 'down'}`}></i>
              </button>
            </th>
            <th>Description</th>
            <th>Site</th>
            <th>Applied to</th>
            <th>Security</th>
            <th className="ctpl-col-actions" aria-label="Actions"></th>
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={7} className="ctpl-empty-row">Loading templates...</td>
            </tr>
          )}
          {!loading && templates.length === 0 && (
            <tr>
              <td colSpan={7} className="ctpl-empty-row">No templates found.</td>
            </tr>
          )}
          {templates.map((row) => (
            <tr key={row.id}>
              <td className="ctpl-col-check">
                <input
                  type="checkbox"
                  checked={selected.has(row.id)}
                  onChange={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (next.has(row.id)) next.delete(row.id);
                      else next.add(row.id);
                      return next;
                    });
                  }}
                />
              </td>
              <td>
                <div className="ctpl-name-cell">
                  <span>{row.name}</span>
                  {row.orgDefault && <em>Org default</em>}
                </div>
              </td>
              <td>{row.description}</td>
              <td className="ctpl-linkish">{row.sites} sites</td>
              <td>{row.appliedTo} checkouts</td>
              <td>{row.security}</td>
              <td className="ctpl-col-actions">
                <button type="button" className="ctpl-row-icon" title="Edit template" onClick={() => openEdit(row)}>
                  <i className="fas fa-pen"></i>
                </button>
                <KebabMenu onDelete={() => deleteTemplate(row)} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination footer */}
      {total > PAGE_SIZE && (
        <div className="list-footer">
          <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}>
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total}
          </span>
          <div className="list-footer-pager">
            <button
              type="button"
              className="pager-btn"
              disabled={offset === 0}
              onClick={() => handleOffsetChange(Math.max(0, offset - PAGE_SIZE))}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button
              type="button"
              className="pager-btn"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => handleOffsetChange(offset + PAGE_SIZE)}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}

      <TemplateModal
        open={modalOpen}
        mode={mode}
        template={editing}
        submitting={submitting}
        onClose={() => {
          if (submitting) return;
          setModalOpen(false);
          setEditing(null);
        }}
        onSave={saveTemplate}
      />

      <PublishModal
        open={publishOpen}
        templateName={pendingDraft?.name || 'template'}
        appliedTo={pendingDraft?.appliedTo ?? 0}
        submitting={submitting}
        onCancel={() => {
          if (submitting) return;
          setPublishOpen(false);
          setPendingDraft(null);
          setEditing(null);
        }}
        onPublish={publishTemplate}
      />
    </>
  );
}

